import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { StickyNote2, StickyNote2Outlined } from '@mui/icons-material';
import { bookingStudioOf } from '../../utils/studioOf';
import { colors } from '../../theme/theme';
import { noteStudioId } from '../../services/userNotesApi';
import type { UserNote } from '../../services/userNotesApi';
import type { NotesMode } from '../UserNoteDialog';
import { SCHED } from './scheduleTheme';

/** The slice of a booking the pending check and note icon read. */
interface RowBooking {
  bookingStatus: string;
  isPaid?: boolean;
  studio?: any;
  user?: { _id: string; name?: string };
  transaction?: {
    basePrice?: number;
    taxes?: number;
    modeOfPayment?: string;
    purchasedPack?: { isPaid?: boolean } | null;
  };
}

/**
 * Money still owed on this booking. A pack booking owes when the pack itself
 * was assigned unpaid; a cancelled booking is never owed, and neither is one
 * with nothing to collect (older free bookings can be stored as unpaid).
 */
export const isPaymentPending = (b: RowBooking): boolean => {
  if (b.bookingStatus === 'CANCELLED') return false;
  const mode = b.transaction?.modeOfPayment;
  if (mode === 'subscription') return b.transaction?.purchasedPack?.isPaid === false;
  const amount = (b.transaction?.basePrice ?? 0) + (b.transaction?.taxes ?? 0);
  return b.isPaid === false && amount > 0;
};

const tagSx = {
  flexShrink: 0,
  fontSize: '12px',
  fontWeight: 700,
  lineHeight: 1,
  p: '5px 9px',
  borderRadius: '999px',
  whiteSpace: 'nowrap',
};

export const PaymentPendingTag: React.FC = () => (
  <Box component="span" sx={{ ...tagSx, backgroundColor: SCHED.waitBg, color: SCHED.waitText }}>
    Payment pending
  </Box>
);

export const TrialTag: React.FC = () => (
  <Box component="span" sx={{ ...tagSx, backgroundColor: SCHED.todayTint, color: SCHED.primaryText }}>
    Trial
  </Box>
);

/** Member notes for the bookings on screen, and how to open one. */
export interface BookingNotes {
  mode: NotesMode;
  notesByUser: Record<string, UserNote[]>;
  open: (anchorEl: HTMLElement, booking: RowBooking) => void;
}

export const noteForBooking = (notes: BookingNotes, b: RowBooking): UserNote | undefined => {
  const at = bookingStudioOf(b);
  return b.user?._id && at ? (notes.notesByUser[b.user._id] || []).find((n) => noteStudioId(n) === at) : undefined;
};

interface MemberNoteIconProps {
  notes: BookingNotes;
  booking: RowBooking;
  size?: number;
  /** Only when a note exists: no "add" icon, for tight spots like name chips. */
  existingOnly?: boolean;
}

/** Same as the Bookings page: filled when there's a note, faint to add one. */
export const MemberNoteIcon: React.FC<MemberNoteIconProps> = ({ notes, booking, size = 20, existingOnly }) => {
  const note = noteForBooking(notes, booking);
  if (!booking.user?._id || !notes.mode || (!note && (existingOnly || notes.mode !== 'studio'))) return null;
  const Icon = note ? StickyNote2 : StickyNote2Outlined;
  return (
    <Tooltip title={note ? <span style={{ whiteSpace: 'pre-wrap' }}>{note.text}</span> : 'Add note'} placement="top" enterDelay={300}>
      <IconButton
        size="small"
        aria-label={note ? 'Member note' : 'Add note'}
        onClick={(e) => {
          e.stopPropagation();
          notes.open(e.currentTarget, booking);
        }}
        onKeyDown={(e) => e.stopPropagation()}
        sx={{
          p: size < 20 ? 0 : 0.5,
          flexShrink: 0,
          color: note ? colors.primary : SCHED.muted,
          opacity: note ? 1 : 0.5,
          '&:hover': { backgroundColor: `${colors.primary}1a`, opacity: 1 },
        }}
      >
        <Icon sx={{ fontSize: size }} />
      </IconButton>
    </Tooltip>
  );
};
